<template>
  <div class="relative isolate">
    <!--
      Single root element on purpose: App.vue wraps pages in
      <Transition mode="out-in">, which cannot track the leave of a fragment
      root. A template comment placed BEFORE this div made the root a fragment
      in dev builds (comments are kept there), and leaving the page stalled the
      router view on an empty <main>. `relative isolate` kept the page above
      App.vue's decorative blobs, which were removed on 2026-09-30.
    -->
    <!-- Hero -->
    <section class="px-4 py-20 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-4xl text-center">
        <h1 class="mb-6 text-4xl font-bold text-theme-fg md:text-6xl">
          WebRTC connections,
          <br>
          <span class="text-theme-brand">signaled by QR code</span>
        </h1>
        <p class="mx-auto mb-8 max-w-3xl text-lg text-theme-neutral-6 md:text-xl">
          Two devices exchange a WebRTC offer and answer by scanning each other's QR codes,
          then talk over a direct data channel. It runs on the browser's native WebRTC with a Vue.js
          interface — there is no signaling server; public STUN servers are used to discover network addresses.
        </p>
        <div class="flex flex-col justify-center gap-4 sm:flex-row">
          <Button as-child size="lg">
            <RouterLink to="/demo" aria-label="Try the WebRTC QR demo now">
              <IconifyIcon icon="heroicons:bolt-20-solid" class="h-5 w-5" />
              Try the demo
            </RouterLink>
          </Button>
          <Button as-child size="lg" variant="outline">
            <RouterLink to="/docs" aria-label="View the documentation">
              <IconifyIcon icon="heroicons:book-open-20-solid" class="h-5 w-5" />
              Documentation
            </RouterLink>
          </Button>
        </div>
      </div>
    </section>

    <!-- Features -->
    <section class="px-4 py-16 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <div class="mb-12 text-center">
          <h2 class="mb-4 text-3xl font-bold text-theme-fg md:text-4xl">
            What it does
          </h2>
          <p class="mx-auto max-w-2xl text-lg text-theme-neutral-6">
            What the web app implements today, and where its limits are.
          </p>
        </div>

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card v-for="feature in features" :key="feature.title">
            <CardHeader>
              <div class="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-theme-brand/10">
                <IconifyIcon :icon="feature.icon" class="h-6 w-6 text-theme-brand" />
              </div>
              <CardTitle>{{ feature.title }}</CardTitle>
            </CardHeader>
            <CardContent class="pt-0 text-theme-neutral-6">
              {{ feature.text }}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>

    <!-- How it works -->
    <section class="px-4 py-16 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <div class="mb-12 text-center">
          <h2 class="mb-4 text-3xl font-bold text-theme-fg md:text-4xl">
            How it works
          </h2>
          <p class="mx-auto max-w-2xl text-lg text-theme-neutral-6">
            The QR codes replace the signaling server; everything else is standard WebRTC.
          </p>
        </div>

        <ol class="grid gap-6 md:grid-cols-3">
          <li v-for="(step, index) in steps" :key="step.title">
            <Card class="h-full">
              <CardHeader>
                <div class="flex items-center gap-3">
                  <Badge variant="primary" size="lg">{{ index + 1 }}</Badge>
                  <CardTitle>{{ step.title }}</CardTitle>
                </div>
              </CardHeader>
              <CardContent class="space-y-4 pt-0">
                <p class="text-theme-neutral-6">
                  {{ step.text }}
                </p>
                <div class="flex items-center justify-center rounded-lg bg-theme-surface-2 p-6">
                  <IconifyIcon :icon="step.icon" class="h-8 w-8 text-theme-brand" />
                </div>
              </CardContent>
            </Card>
          </li>
        </ol>

        <!-- Technical flow -->
        <Card class="mt-12">
          <CardHeader>
            <CardTitle as="h3" class="text-center text-2xl">
              Technical flow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol class="flex flex-col items-center gap-4 md:flex-row md:justify-center">
              <template v-for="(node, index) in flow" :key="node.label">
                <li class="flex flex-col items-center text-center">
                  <IconifyIcon :icon="node.icon" class="mb-2 h-8 w-8 text-theme-brand" />
                  <span class="text-sm font-medium text-theme-fg">{{ node.label }}</span>
                  <span class="text-xs text-theme-neutral-6">{{ node.detail }}</span>
                </li>
                <li
                  v-if="index < flow.length - 1"
                  aria-hidden="true"
                  class="text-2xl text-theme-neutral-6 md:rotate-0 rotate-90"
                >
                  →
                </li>
              </template>
            </ol>
          </CardContent>
        </Card>
      </div>
    </section>

    <!-- What you can try -->
    <section class="px-4 py-16 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <div class="mb-12 text-center">
          <h2 class="mb-4 text-3xl font-bold text-theme-fg md:text-4xl">
            Try it
          </h2>
          <p class="mx-auto max-w-2xl text-lg text-theme-neutral-6">
            Screens in this app that open a real connection between two devices.
          </p>
        </div>

        <div class="grid gap-6 md:grid-cols-3">
          <Card v-for="entry in tryIt" :key="entry.to" class="flex flex-col">
            <CardHeader>
              <div class="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-theme-brand/10">
                <IconifyIcon :icon="entry.icon" class="h-6 w-6 text-theme-brand" />
              </div>
              <CardTitle>{{ entry.title }}</CardTitle>
            </CardHeader>
            <CardContent class="flex-1 pt-0 text-theme-neutral-6">
              {{ entry.text }}
            </CardContent>
            <CardFooter>
              <Button as-child variant="outline" size="sm">
                <RouterLink :to="entry.to">
                  {{ entry.cta }}
                  <IconifyIcon icon="heroicons:arrow-right-20-solid" class="h-4 w-4" />
                </RouterLink>
              </Button>
            </CardFooter>
          </Card>
        </div>

        <Alert variant="info" class="mx-auto mt-8 max-w-3xl">
          <AlertTitle>Not available yet</AlertTitle>
          <AlertDescription>
            Multi-party video calls are not implemented: they need a signaling server, which this app does not have.
          </AlertDescription>
        </Alert>
      </div>
    </section>

    <!-- CTA -->
    <section class="px-4 py-16 sm:px-6 lg:px-8">
      <Card variant="brand" class="mx-auto max-w-4xl text-center">
        <CardHeader>
          <CardTitle as="h2" class="text-3xl md:text-4xl">
            Connect two devices
          </CardTitle>
          <CardDescription class="text-lg">
            Open the demo on two devices, or in two browser tabs, and follow the steps.
          </CardDescription>
        </CardHeader>
        <CardContent class="flex justify-center">
          <Button as-child size="lg">
            <RouterLink to="/demo" aria-label="Try the WebRTC QR demo">
              <IconifyIcon icon="heroicons:rocket-launch-20-solid" class="h-5 w-5" />
              Try the demo
            </RouterLink>
          </Button>
        </CardContent>
      </Card>
    </section>
  </div>
</template>

<script setup lang="ts">
import { Alert, AlertDescription, AlertTitle } from '@elveris/ui/alert'
import { Badge } from '@elveris/ui/badge'
import { Button } from '@elveris/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@elveris/ui/card'
import { RouterLink } from 'vue-router'
import IconifyIcon from '../components/IconifyIcon.vue'

// Every statement below is backed by src/utils/webrtc-native.ts and
// src/utils/ice-config.ts; see the comments on each entry.
const features = [
  {
    icon: 'heroicons:qr-code-20-solid',
    title: 'No signaling server',
    // createHostConnection / acceptConnection serialize the SDP into the QR payload.
    text: 'The WebRTC offer and answer travel as QR codes (or copy-paste), so no signaling server is involved in setting up the connection.',
  },
  {
    icon: 'heroicons:shield-check-20-solid',
    title: 'Encrypted in transit',
    // WebRTC mandates DTLS for data channels; the QR payload is plain JSON.
    text: 'WebRTC always encrypts data channels with DTLS; the browser does this, not app code. The QR payload itself (the SDP offer and answer) is not encrypted.',
  },
  {
    icon: 'heroicons:device-phone-mobile-20-solid',
    title: 'Runs in the browser',
    text: 'Needs a modern browser with WebRTC. A camera is used to scan codes; without one, the offer and answer can be pasted as text.',
  },
  {
    icon: 'heroicons:arrow-path-20-solid',
    title: 'Direct data channel',
    // getIceServers(): STUN only; no ConnectivityGrant fetcher is registered, so no TURN.
    text: 'Data flows peer to peer over an RTCDataChannel. The app configures no TURN relay today, so no server relays your data.',
  },
  {
    icon: 'heroicons:globe-americas-20-solid',
    title: 'NAT traversal via STUN',
    // STATIC_STUN_URLS in ice-config.ts: stun.elveris.work, stun.cloudflare.com.
    text: 'STUN servers (Elveris\'s own, with Cloudflare\'s as fallback) discover each device\'s public address. Without a TURN relay, some restrictive networks and NATs cannot connect.',
  },
  {
    icon: 'heroicons:wifi-20-solid',
    title: 'Works offline on a local network',
    // getIceServers() returns [] when navigator.onLine is false.
    text: 'When the browser reports it is offline, no ICE servers are used and the devices connect with local host candidates only.',
  },
]

const steps = [
  {
    icon: 'heroicons:qr-code-20-solid',
    title: 'Host shows an offer',
    text: 'The host creates a WebRTC offer, gathers ICE candidates and shows the offer as a QR code.',
  },
  {
    icon: 'heroicons:camera-20-solid',
    title: 'Guest scans and answers',
    text: 'The guest scans the offer, creates an answer and shows it as a QR code.',
  },
  {
    icon: 'heroicons:link-20-solid',
    title: 'Host scans the answer',
    text: 'The host applies the answer, ICE finds a path and the data channel opens. The app reports "connected" only when the channel is open.',
  },
]

const flow = [
  { icon: 'heroicons:document-text-20-solid', label: 'SDP offer', detail: 'RTCPeerConnection' },
  // waitForIceCandidates(): 300 ms deadline, proceeds with the candidates it has.
  { icon: 'heroicons:signal-20-solid', label: 'ICE candidates', detail: 'up to 300 ms' },
  { icon: 'heroicons:qr-code-20-solid', label: 'QR code', detail: 'JSON payload' },
  { icon: 'heroicons:arrow-uturn-left-20-solid', label: 'SDP answer', detail: 'QR code back' },
  { icon: 'heroicons:lock-closed-20-solid', label: 'Data channel', detail: 'DTLS' },
]

const tryIt = [
  {
    to: '/chat',
    icon: 'heroicons:chat-bubble-left-right-20-solid',
    title: 'Chat',
    text: 'Text messages between two devices over the data channel.',
    cta: 'Open chat',
  },
  {
    to: '/file-transfer',
    icon: 'heroicons:document-arrow-up-20-solid',
    title: 'File transfer',
    text: 'Send a file directly to the other device in chunks over the data channel.',
    cta: 'Open file transfer',
  },
  {
    to: '/demo',
    icon: 'heroicons:bolt-20-solid',
    title: 'Demo',
    text: 'Step through the offer and answer exchange and watch the connection state.',
    cta: 'Open demo',
  },
]
</script>
